
import type { StatsResponse } from '@empathybroker/tsclient-stats/dist/models/stats-response'
import type { ApiError, Sortable } from '@empathyco/playboard-core-domain'
import type {
  ContextState,
  DataTableColumn,
  Pagination,
  RelatedPromptsAnalyticsMessages,
} from '../../models'

import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum'

import { computed, defineComponent, getCurrentInstance, ref, watch } from 'vue'
import DataTable from '../../components/crud/data-table.component.vue'
import PaginationBar from '../../components/crud/pagination-bar.component.vue'
import InsightsNoResults from '../../components/insights/insights-no-results.component.vue'
import RelatedPromptsDistributionChart from '../../components/insights/related-prompts/related-prompts-distribution-chart.component.vue'
import { UiProgressLinear } from '../../components/keen-ui'
import { EpbText } from '../../components/shared/epb-text'

import EpbTabs from '../../components/tabs.component.vue'
import { mapActions, mapGetters } from '../../composables'
import { useTextTransform } from '../../composables/use-text-transform'
import { AlignEnum, OriginsEnum, SortDirectionEnum } from '../../enums'
import { ANALYTICS_CONSTS } from '../../store/analytics/analytics.consts'
import { CONTEXT_CONSTS } from '../../store/context/context.consts'
import { DownloadUtils } from '../../utils/download.utils'

interface RelatedPromptBase {
  id: string
  level: number
  query: string
  click_count: number
  click_child_count: number
  a2cChildCount: number
}

interface RelatedPromptItem extends RelatedPromptBase {
  children?: RelatedPromptBase[]
}

export default defineComponent({
  name: 'RelatedPromptsAnalytics',
  components: {
    EpbText,
    EpbTabs,
    InsightsNoResults,
    DataTable,
    PaginationBar,
    RelatedPromptsDistributionChart,
    UiProgressLinear,
  },
  setup() {
    const baseClass = 'related-prompts-analytics'
    const { typeWriter } = useTextTransform()

    const ctx = getCurrentInstance()
    if (!ctx) {
      throw new Error("'ctx' is not defined")
    }

    const { fetchStatsReport, loadDistData } = mapActions(
      ctx.proxy.$store,
      ANALYTICS_CONSTS.moduleName,
      {
        fetchStatsReport: ANALYTICS_CONSTS.actions.fetchStatsReport,
        loadDistData: ANALYTICS_CONSTS.actions.loadDistData,
      },
    )

    const { analyticsData } = mapGetters(ctx.proxy.$store, ANALYTICS_CONSTS.moduleName, {
      analyticsData: ANALYTICS_CONSTS.getters.data,
    })

    const { context } = mapGetters<ContextState>(ctx.proxy.$store, CONTEXT_CONSTS.moduleName, {
      context: CONTEXT_CONSTS.getters.context,
    })

    const messages: RelatedPromptsAnalyticsMessages =
      ctx.proxy.$epb.messages.insights.relatedPrompts

    const itemsPerPageOptions = [10, 50, 100, 200]

    const pagination = ref({
      page: 1,
      itemsPerPage: itemsPerPageOptions[0],
      totalItems: 0,
    })

    const formatNumber = (number: number): string => {
      return ctx.proxy.$epb.utils.formatNumber(number, { maximumFractionDigits: 2 }, true)
    }

    const tabsOptions = [
      { id: OriginsEnum.OVERALL, text: messages.tabs.overall },
      { id: OriginsEnum.NO_RESULTS, text: messages.tabs.noResults },
      { id: OriginsEnum.LOW_RESULTS, text: messages.tabs.lowResults },
    ]

    const selectedTab = ref(tabsOptions[0].id)
    const loading = ref(false)
    const loadingTable = ref(false)
    const data = ref()
    const expandedChildrenIds = ref<Array<string>>([])
    const sort = ref<Sortable['sort']>({
      field: 'click_child_count',
      direction: SortDirectionEnum.DESCENDING,
    })
    const tableData = ref([])

    const hasNoResults = computed(() => !loading.value && !data.value?.queryCount)

    const totalQueriesCount = computed(
      (): number => analyticsData.value.overall.dist.currentPeriod?.data.queryCount,
    )

    const columns = ref<Array<DataTableColumn>>([
      {
        id: 'query',
        text: messages.table.relatedPrompts,
        width: '40%',
      },
      {
        id: 'click_count',
        text: messages.table.promptsClicks,
        sortable: true,
        width: '15%',
        align: AlignEnum.CENTER,
      },
      {
        id: 'click_child_count',
        text: messages.table.clicks,
        sortable: true,
        width: '15%',
        align: AlignEnum.CENTER,
      },
      {
        id: 'a2cChildCount',
        text: messages.table.add2carts,
        width: '15%',
        align: AlignEnum.CENTER,
      },
    ])

    const fetchData = async (
      origins = [
        'related_prompts:results',
        'related_prompts:no_results',
        'related_prompts:low_results',
        'related_prompts:none',
        'overview:none',
        'overview:results',
      ],
    ): Promise<StatsResponse> => {
      const response: StatsResponse = await fetchStatsReport({
        id: StatsReportsIdsEnum.DAILY_ORIGINS,
        params: {
          origins,
        },
      })
      return response.data
    }

    const getPaginationData = function (paginationParam: Pagination): {
      offset: number
      rows: number
    } {
      return {
        offset: (pagination.value.page - 1) * pagination.value.itemsPerPage,
        rows: paginationParam.itemsPerPage,
      }
    }

    const fetchRelatedPromptQueries = async relatedPrompt => {
      const queryResponse = await fetchStatsReport({
        id: StatsReportsIdsEnum.TOOLS_BY_QUERY,
        params: {
          queries: relatedPrompt.queries,
          parentToolId: relatedPrompt.toolId,
        },
      })

      return queryResponse.data.dist.map(item => ({
        id: relatedPrompt.toolId + item.toolId,
        level: 1,
        query: item.toolName,
        click_child_count: item.clickCount,
        a2cChildCount: item.add2cartCount,
        click_count: '-',
      }))
    }

    const convertToCSV = (data: RelatedPromptItem[]): string => {
      const csvRows = ['id,level,related prompt,related prompt click,click,a2cChildCount,parentId']

      function flattenData(item: RelatedPromptItem, parentId?: string) {
        const { id, level, query, click_count, click_child_count, a2cChildCount, children } = item
        csvRows.push(
          `${level === 0 ? id : ''},${level},"${query}",${click_count},${click_child_count},${a2cChildCount},${parentId ?? ''}`,
        )

        if (children?.length) {
          children.forEach(child => flattenData(child, id))
        }
      }

      data.forEach(item => flattenData(item))

      return csvRows.join('\n')
    }

    const fetchTableData = async (
      paginationParams = pagination.value,
    ): Promise<RelatedPromptItem[]> => {
      const paginationData = getPaginationData(paginationParams)

      const response: StatsResponse = await fetchStatsReport({
        id: StatsReportsIdsEnum.TOOLS,
        params: {
          ...paginationData,
          tools: ['related_prompts'],
          sortField: sort.value?.field,
          sortOrder: sort.value?.direction,
          filter_prompts: true,
        },
      })

      return Promise.all(
        response.data?.map(async relatedPrompt => {
          const children = await fetchRelatedPromptQueries(relatedPrompt)
          return {
            id: relatedPrompt.toolId,
            level: 0,
            query: relatedPrompt.toolName,
            click_child_count: relatedPrompt.clickChildCount,
            a2cChildCount: relatedPrompt.add2cartChildCount,
            click_count: relatedPrompt.clickCount,
            children,
          }
        }) ?? [],
      )
    }

    const fetchPaginationTableData = async (): Promise<number> => {
      const response: StatsResponse = await fetchStatsReport({
        id: StatsReportsIdsEnum.TOOLS_PAGINATION,
        params: {
          tools: ['related_prompts'],
          filter_prompts: true,
        },
      })
      return response.data[0]?.toolsCount ?? 0
    }

    const onPaginationChange = async (paginationParam: Pagination): Promise<void> => {
      pagination.value = paginationParam
      try {
        loadingTable.value = true
        tableData.value = await fetchTableData()
        fetchPaginationTableData().then(count => {
          pagination.value.totalItems = count
        })
      } catch (error) {
        ctx.proxy.$emit('showError', ctx.proxy.$epb.utils.decodeErrorMessage(error as ApiError))
      } finally {
        loadingTable.value = false
      }
    }

    const onDownload = async (paginationParam: Pagination): Promise<void> => {
      const downloadData = await fetchTableData(paginationParam)
      DownloadUtils.downloadCSV(convertToCSV(downloadData), messages.downloadFile)
    }

    const loadData = async (): Promise<void> => {
      loading.value = true
      try {
        analyticsData.value.overall.dist.currentPeriod = undefined
        await loadDistData()
        if (selectedTab.value === OriginsEnum.OVERALL) {
          data.value = await fetchData()
          loading.value = false
          tableData.value = await fetchTableData()
          fetchPaginationTableData().then(count => {
            pagination.value.totalItems = count
          })
        } else if (selectedTab.value === OriginsEnum.NO_RESULTS) {
          data.value = await fetchData(['related_prompts:no_results'])
        } else {
          data.value = await fetchData(['related_prompts:low_results'])
        }
      } catch (error) {
        ctx.proxy.$emit('showError', ctx.proxy.$epb.utils.decodeErrorMessage(error as ApiError))
      } finally {
        loading.value = false
      }
    }

    const summary = computed(() =>
      ctx.proxy.$epb.utils.compileMessage(messages.summary, {
        clicks: formatNumber(data.value?.dist.reduce((sum, item) => sum + item?.clickCount, 0)),
        add2carts: formatNumber(
          data.value?.dist.reduce((sum, item) => sum + item?.add2cartCount, 0),
        ),
        queries: formatNumber(totalQueriesCount.value),
      }),
    )

    // Text animation
    const animatedSummary = ref('')
    let typeWriterAnimation: ReturnType<typeof typeWriter> | null = null

    watch(loading, isLoading => {
      if (!isLoading && summary.value) {
        if (typeWriterAnimation) {
          typeWriterAnimation.stop()
        }

        typeWriterAnimation = typeWriter(summary.value, 20, text => {
          animatedSummary.value = text
        })

        typeWriterAnimation.start()
      }
    })

    watch(
      () => [context.value.filters, selectedTab.value],
      () => {
        pagination.value.page = 1
        loadData()
      },
      { immediate: true },
    )

    watch(
      () => sort.value,
      async () => {
        try {
          loadingTable.value = true
          tableData.value = await fetchTableData()
        } catch (error) {
          ctx.proxy.$emit('showError', ctx.proxy.$epb.utils.decodeErrorMessage(error as ApiError))
        } finally {
          loadingTable.value = false
        }
      },
    )

    return {
      baseClass,
      messages,
      pagination,
      animatedSummary,
      itemsPerPageOptions,
      tabsOptions,
      selectedTab,
      OriginsEnum,
      loading,
      loadingTable,
      hasNoResults,
      columns,
      tableData,
      expandedChildrenIds,
      sort,
      SortDirectionEnum,
      formatNumber,
      onPaginationChange,
      onDownload,
    }
  },
})
